import { useTranslation } from 'react-i18next';
import { CustomModal } from '../../CustomModal';
import { ASNavButton } from './ASNavButton';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ASParamViewer } from './ASParamViewer';
import API from '../../../service/api';
import { LoadingSpinner } from '../../LoadingSpinner';
import { Alert } from '../../Alert';
import { ws } from '../../../service/ws';
import { getParamConfig } from './ASParamEditor';
import { MDBBtn, MDBIcon } from 'mdb-react-ui-kit';
import { MachineConfigParamDTO } from '@scm/common-legacy';

export interface ParamData {
    param: MachineConfigParamDTO;
    value: any | null;
    reading: boolean;
    writing: boolean;
    error?: string;
}

export interface AdvancedSettingsProps {
    open: boolean;
    onClose: () => void;
    groupId?: number;
    locationId?: number;
    machineId: number;
}

export const AdvancedSettings = ({
    open,
    onClose,
    groupId,
    locationId,
    machineId,
}: AdvancedSettingsProps) => {
    const { t } = useTranslation();

    const [params, setParams] = useState<ParamData[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [isMachineReseting, setIsMachineReseting] = useState(false);
    const [error, setError] = useState<any>(null);
    const [selectedParam, setSelectedParam] = useState<ParamData | null>(null);

    const uri = useMemo(
        () =>
            groupId
                ? `/api/groups/${groupId}/${locationId}/${machineId}`
                : `/api/admmachines/${machineId}`,
        [groupId, locationId, machineId],
    );

    const updateParamData = useCallback(
        (name: string, newData: Partial<ParamData>) => {
            setParams((prev) =>
                !prev
                    ? prev
                    : prev.map((p) =>
                          p.param.name !== name
                              ? p
                              : {
                                    ...p,
                                    ...newData,
                                },
                      ),
            );
            setSelectedParam((prev) =>
                prev?.param.name !== name
                    ? prev
                    : {
                          ...prev,
                          ...newData,
                      },
            );
        },
        [],
    );

    const onAddrWritten = useCallback(
        (name: string) => {
            updateParamData(name, { writing: false, value: null });
        },
        [updateParamData],
    );

    const onAddrRead = useCallback((name: string, value: any) => {
        updateParamData(name, {
            reading: false,
            value,
        });
    }, []);

    const onMachineReset = useCallback(() => {
        setIsMachineReseting(false);
    }, []);

    const readData = useCallback(
        (name: string) => {
            updateParamData(name, { reading: true, error: '' });

            API.POST(`${uri}/param/read`, { name }).catch((err) => {
                updateParamData(name, { reading: false });
                alert(err.error ?? err.message);
            });
        },
        [uri, updateParamData],
    );

    const writeData = useCallback(
        (name: string, val: any) => {
            updateParamData(name, { writing: true, error: '' });

            API.POST(`${uri}/param/write`, { name, val })
                .then(() => {
                    setTimeout(() => onAddrWritten(name), 2250); //test;
                })
                .catch((err) => {
                    updateParamData(name, { writing: false });
                    alert(err.error ?? err.message);
                });
        },
        [uri, updateParamData],
    );

    const resetMachine = useCallback(() => {
        setIsMachineReseting(true);
        API.POST(`${uri}/action`, { id: 'reset' });
    }, [uri]);

    useEffect(() => {
        setParams(null);
        setSelectedParam(null);
        setError(null);
        setIsMachineReseting(false);
        if (!open) return;
        setLoading(true);
        API.GET(`${uri}/advanced`)
            .then((paramsDTO: MachineConfigParamDTO[]) =>
                setParams(
                    paramsDTO.map((param) => ({
                        param,
                        value: null,
                        reading: false,
                        writing: false,
                    })),
                ),
            )
            .catch(setError)
            .finally(() => setLoading(false));
    }, [open, uri]);

    useEffect(() => {
        let unlistenList: (() => void)[] = [];

        if (open) {
            unlistenList = [
                ws.on('advanced_settings_read', ({ name, value }: {name: string, value: any}) => {
                    onAddrRead(name, value);
                }),
                ws.on('advanced_settings_write', ({ name }: { name: string }) => {                
                    onAddrWritten(name);
                }),
                ws.on('advanced_settings_reset', () => {
                    onMachineReset();
                })
            ];

            ws.send('advanced_settings', { groupId, locationId, machineId });
        }

        return () => {
            ws.send('advanced_settings_leave', { groupId, locationId, machineId });
            unlistenList.forEach((unlisten) => unlisten());
        };

        // let unlisten: (() => void) | null = null;
        // if (open) {
        //     unlisten = WS.advancedSettingsListener({
        //         groupId,
        //         locationId,
        //         machineId,
        //         onWrite: ({ name }: any) => {
        //             // value written
        //             onAddrWritten(name);
        //         },
        //         onRead: ({ name, value }: any) => {
        //             // on value read
        //             onAddrRead(name, value);
        //         },
        //         onMachineReset: () => {
        //             // on machine reset
        //             onMachineReset();
        //         },
        //     });
        // }
        // return () => {
        //     if (unlisten) unlisten();
        // };
    }, [open, groupId, locationId, machineId]);

    useEffect(() => {
        if (params && !selectedParam) setSelectedParam(params[0]);
    }, [params, selectedParam]);

    return (
        <CustomModal
            open={open}
            onClose={onClose}
            title={t('advanced_settings')}
            header={
                locationId &&
                machineId && (
                    <div>
                        <MDBBtn
                            color="secondary"
                            className="py-2 ms-3 d-flex flex-row align-items-center"
                            onClick={resetMachine}
                            disabled={isMachineReseting}
                        >
                            <span
                                className={`me-1${isMachineReseting ? ' d-none' : ''}`}
                            >
                                <MDBIcon icon="sync" />
                            </span>
                            <span
                                className={`me-1${!isMachineReseting ? ' d-none' : ''}`}
                            >
                                <LoadingSpinner size="sm" className="p-0" />
                            </span>
                            <span>{t('reset')}</span>
                        </MDBBtn>
                    </div>
                )
            }
            dialogStyle={{ maxWidth: 800, height: 500 }}
            bodyStyle={{
                overflowX: 'hidden',
                overflowY: 'hidden',
                maxWidth: 800,
                height: 500,
                padding: 0,
            }}
            staticBackdrop
        >
            {loading && <LoadingSpinner />}
            {error && (
                <div className="d-flex flex-fill justify-content-center align-items-center">
                    <Alert message={error.message} type="danger" />
                </div>
            )}
            {params && (
                <div className="d-flex flex-row w-100 h-100 overflow-auto">
                    <div
                        className="list-group list-group-flush h-100 p-2 overflow-auto"
                        style={{ flex: 3, minWidth: 200 }}
                    >
                        {params.map((p) => (
                            <ASNavButton
                                key={p.param.name}
                                param={p}
                                active={
                                    p.param.name == selectedParam?.param.name
                                }
                                icon={getParamConfig(p.param)?.icon ?? 'wrench'}
                                onClick={() => setSelectedParam(p)}
                            />
                        ))}
                    </div>
                    {selectedParam && (
                        <div
                            className="overflow-hidden"
                            style={{ flex: 5, minWidth: 350 }}
                        >
                            <ASParamViewer
                                key={selectedParam.param.addr}
                                param={selectedParam}
                                icon={
                                    getParamConfig(selectedParam.param)?.icon ??
                                    'wrench'
                                }
                                onRead={() =>
                                    readData(selectedParam.param.name)
                                }
                                onWrite={(val) =>
                                    writeData(selectedParam.param.name, val)
                                }
                            />
                        </div>
                    )}
                </div>
            )}
        </CustomModal>
    );
};
